import { DataModelValue, FormValue, NUMBER_SYSTEM, Question } from "@archetypes/form";

export const isWebWorkerSupported = typeof window.Worker !== "undefined";

export const range = (start: number, end?: number, step?: number): number[] => {
  if (typeof end === "undefined") {
    end = start;
    start = 0;
  }
  const updatedStep = step || 1;
  const length = Math.max(Math.ceil((end - start) / updatedStep), 0);
  const result = new Array(length);

  for (let i = 0; i < length; i++) {
    result[i] = start + i * updatedStep;
  }

  return result;
};

export const shuffle = <T>(arr: T[]): T[] => {
  const array = [...arr];
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
};

export function filterSubsetQuestions(questions: Question[], subset: Question[]): Question[] {
  if (questions.length === 0) {
    return [];
  }

  // Create a set of subset array IDs for constant time lookup
  const subsetIds = new Set(subset.map((question) => question.id));

  // Filter the parent array using the subset IDs for efficient filtering
  return questions.filter((question) => subsetIds.has(question.id));
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getUniqueListBy<T extends Record<string, any>>(arr: T[], key: string) {
  if (Array.isArray(arr)) {
    return [...new Map(arr.map((item) => [item[key] as T, item])).values()];
  }
  return arr;
}

export function dataURItoFile(dataURI: string, fileName: string) {
  // convert base64/URLEncoded data component to raw binary data held in a string
  let byteString;
  if (dataURI.split(",")[0].indexOf("base64") >= 0) byteString = atob(dataURI.split(",")[1]);
  else byteString = unescape(dataURI.split(",")[1]);
  // separate out the mime component
  const mimeString = dataURI.split(",")[0].split(":")[1].split(";")[0];
  // write the bytes of the string to a typed array
  const ia = new Uint8Array(byteString.length);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  return new File([new Blob([ia], { type: mimeString })], fileName, {
    type: "image/png",
  });
}

export function cleanObject<T>(data: T): T {
  if (Array.isArray(data)) {
    // Recursively clean each element in the array
    return data
      .map((item) => cleanObject(item))
      .filter((item) => item !== null && item !== undefined) as T;
  } else if (data !== null && typeof data === "object") {
    // Recursively clean each key-value pair in the object
    return Object.entries(data).reduce((acc, [key, value]) => {
      const cleanedValue = cleanObject(value);
      if (cleanedValue !== null && cleanedValue !== undefined) {
        (acc as Record<string, unknown>)[key] = cleanedValue;
      }
      return acc;
    }, {} as T);
  }
  // Return the value itself if it's not an object or array
  return data;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isNumeric(val: any): boolean {
  return !(val instanceof Array) && val - parseFloat(val) + 1 >= 0;
}

export function removeHTMLTagsAndCharacterEntities(val: string): string {
  return val.replace(/(&#?\w+;|<[^>]*>)/gi, "");
}

export function BinarySearch<T>(elem: T, arr: T[], compare: (a: T, b: T) => number): boolean {
  let start = 0;
  let end = arr.length - 1;

  while (start <= end) {
    const mid = Math.floor((start + end) / 2);

    if (compare(arr[mid], elem) === 0) {
      return true;
    } else if (compare(arr[mid], elem) < 0) {
      start = mid + 1;
    } else {
      end = mid - 1;
    }
  }
  return false;
}

export function isSubsetArray<T>(arr1: T[], arr2: T[], compare: (a: T, b: T) => number): boolean {
  if (!Array.isArray(arr1) || !Array.isArray(arr2)) {
    return false;
  }

  if (arr1.length > arr2.length) return false;

  arr2.sort(compare);

  for (let i = 0; i < arr1.length; i++) {
    if (!BinarySearch(arr1[i], arr2, compare)) {
      return false;
    }
  }
  return true;
}

export function haveCommonElement<T>(arr1: T[] = [], arr2: T[] = [], key?: keyof T): boolean {
  const set = new Set<string | T>();

  for (const item of arr1) {
    if (key && typeof item === "object" && item !== null) {
      set.add(JSON.stringify(item[key])); // Store by unique key
    } else {
      set.add(item as T);
    }
  }

  for (const item of arr2) {
    if (key && typeof item === "object" && item !== null) {
      if (set.has(JSON.stringify(item[key]))) {
        return true;
      }
    } else if (set.has(item as T)) {
      return true;
    }
  }

  return false;
}

export function formatString(
  input?: string,
  format: "capital" | "lowercase" | "camel" | "kebab" | "snake" | "pascal" | "uppercase" = "camel",
): string {
  if (!input) {
    return input || "";
  }
  switch (format) {
    case "capital":
      return input.charAt(0).toUpperCase() + input.slice(1).toLowerCase();
    case "lowercase":
      return input.toLowerCase();
    case "uppercase":
      return input.toUpperCase();
    case "camel":
      return input
        .replace(/[^a-zA-Z0-9]+(.)?/g, (_, char) => (char ? char.toUpperCase() : ""))
        .replace(/^./, (char) => char.toLowerCase());
    case "kebab":
      return input.toLowerCase().replace(/\s+/g, "-");
    case "snake":
      return input.toLowerCase().replace(/\s+/g, "_");
    case "pascal":
      return input
        .replace(/[^a-zA-Z0-9]+(.)?/g, (_, char) => (char ? char.toUpperCase() : ""))
        .replace(/^./, (char) => char.toUpperCase());
    default:
      throw new Error("Invalid format type");
  }
}

export const formatNumber = (numStr: string, system?: NUMBER_SYSTEM): string => {
  const num = parseFloat(numStr);
  if (isNaN(num) || !system) return numStr;

  const locales: Record<typeof system, string> = {
    International: "en-US",
    Indian: "en-IN",
    Arabic: "ar-EG",
    French: "fr-FR",
    German: "de-DE",
    Spanish: "es-ES",
    Italian: "it-IT",
  };

  return num.toLocaleString(locales[system]);
};

export const trimString = (str?: string | number): string => {
  if (typeof str !== "string" && typeof str !== "number") {
    return "";
  }
  return String(str).trim();
};

/**
 * Retrieves a nested property value from an object using a dot-separated string path.
 *
 * @param {Record<string, any> | null | undefined} obj The object to traverse.
 * It can be an object with string keys and any type of values, or null/undefined.
 * @param {string} path The dot-separated path to the desired property (e.g., "a.b.c").
 * @returns {any} The value of the property if found, otherwise undefined.
 * Using `any` for the return type to reflect that the value can be of any type.
 */
export function getProperty(
  obj: Record<string, unknown> | null | undefined,
  path: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Record<string, any> | undefined {
  // If the object is null or undefined, we can't proceed, so return undefined.
  // The path parameter is already typed as string, so a typeof check for path is less critical
  // in a pure TypeScript environment but doesn't harm for robustness if called from JS.
  if (obj === null || obj === undefined) {
    return undefined;
  }

  // If the path is not a string (e.g. null or undefined passed from JS),
  // handle it. TypeScript would usually catch this at compile time if path is typed.
  // However, for robustness if this function might be called from non-type-checked JS:
  if (typeof path !== "string") {
    return undefined;
  }

  // Split the path string into an array of keys.
  // E.g., "c.d.e" becomes ["c", "d", "e"]
  const keys = path.split(".");

  // Use the reduce method to traverse the object.
  // The accumulator (currentValue) starts as the initial object.
  // `currentValue` is typed as `any` because its type can change as we delve deeper.

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const value = keys.reduce((currentValue: Record<string, any>, key: string) => {
    // The optional chaining operator (?.) is crucial:
    // - If currentValue is null or undefined, currentValue?.[key] evaluates to undefined.
    // - If key does not exist on currentValue, currentValue?.[key] is also undefined.
    return currentValue?.[key];
  }, obj); // Initial value for reduce is the input object `obj`

  return value;
}

/**
 * Sets a value at a nested path within an object.
 * If any part of the path (except the final property) does not exist or is not an object,
 * an error is thrown.
 *
 * @param {Record<string, any> | null | undefined} obj The object to modify.
 * @param {string} path The dot-separated path to the property to set (e.g., "a.b.c").
 * @param {any} value The value to set at the specified path.
 * @throws {Error} If the object is null/undefined, path is invalid, or path segment is not found/not an object.
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function setProperty<T extends Record<string, any>>(
  obj: Readonly<T | null | undefined>,
  path: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any,
) {
  // Validate the input object
  if (obj === null || obj === undefined) {
    throw new Error("Input object cannot be null or undefined.");
  }
  // It's good practice to ensure the base 'obj' is actually an object type if path has segments.
  // However, the loop below will also catch if 'obj' is not an object for single-segment paths.

  // Validate the path string
  if (typeof path !== "string" || path.trim() === "") {
    throw new Error("Path must be a non-empty string.");
  }

  const keys = path.split(".");
  const newObj: T =
    typeof structuredClone === "function" ? structuredClone(obj) : JSON.parse(JSON.stringify(obj));
  let current = newObj; // 'current' will point to parts of 'newObj'

  // Traverse the path up to the second-to-last key
  // This loop ensures that each segment in the path (except the last) refers to an existing object.
  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];

    // Check if the current segment exists and is an object
    // Note: typeof null is 'object' in JavaScript, so we also check for null explicitly.
    if (
      typeof current !== "object" ||
      current === null ||
      !Object.prototype.hasOwnProperty.call(current, key)
    ) {
      throw new Error(
        `Invalid path: Segment '${keys
          .slice(0, i + 1)
          .join(".")}' not found or is not a valid object.`,
      );
    }

    current = current[key]; // Move to the next level
  }

  // Set the value at the last key
  const lastKey = keys[keys.length - 1];

  // Ensure the final target for property setting is an object
  if (typeof current !== "object" || current === null) {
    if (keys.length === 1) {
      // This means the original 'obj' itself was not an object.
      throw new Error(
        `Cannot set property '${lastKey}': The provided input object is not an object type.`,
      );
    } else {
      // This means an intermediate path resolved to a non-object, which should have been caught above,
      // but this is a safeguard.
      const basePath = keys.slice(0, keys.length - 1).join(".");
      throw new Error(
        `Invalid path: Cannot set property '${lastKey}'. Segment '${basePath}' is not an object.`,
      );
    }
  }

  (current as Record<string, unknown>)[lastKey] = value; // Set the value
  return newObj;
}

// Helper to get focusable children
export function getFocusableElements(container: HTMLElement): HTMLElement[] {
  const focusableSelectors = [
    "button:not([disabled])",
    "[href]",
    'input:not([disabled]):not([type="hidden"])',
    "select:not([disabled])",
    "textarea:not([disabled])",
    '[tabindex]:not([tabindex="-1"])',
  ];
  return Array.from(container.querySelectorAll(focusableSelectors.join(","))) as HTMLElement[];
}

export function getLogoBackgroundColor(hex: string): string {
  // Normalize hex
  let normalizedHex = hex.replace("#", "");

  // Handle shorthand (#RGB → #RRGGBB)
  if (normalizedHex.length === 3) {
    normalizedHex = normalizedHex
      .split("")
      .map((ch) => ch + ch)
      .join("");
  }

  if (normalizedHex.length !== 6) {
    throw new Error("Invalid HEX color.");
  }

  // Convert to RGB
  const r = parseInt(normalizedHex.substring(0, 2), 16);
  const g = parseInt(normalizedHex.substring(2, 4), 16);
  const b = parseInt(normalizedHex.substring(4, 6), 16);

  // Calculate luminance (per ITU-R BT.709)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  // Return black for light colors, white for dark
  return luminance > 0.5 ? "#000000" : "#FFFFFF";
}

export function getDataModelPropertyValue(
  value: Array<DataModelValue> = [],
  path: string,
): FormValue[] | undefined {
  const keys = path.split(".");
  if (!value.length || !keys.length) {
    return;
  }
  return value.map((item) => {
    const currentObj = item.responseData ?? {};

    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      if (!Object.prototype.hasOwnProperty.call(currentObj, key)) {
        return null;
      }
      const cValue = currentObj?.[key] as FormValue;
      if (!isDataModelValue(cValue)) {
        return cValue;
      } else {
        if (i < keys.length - 1) {
          return getDataModelPropertyValue(
            cValue as DataModelValue[],
            keys.slice(i + 1).join("."),
          )?.flat(Infinity) as FormValue;
        }
        return cValue;
      }
    }
  }) as FormValue[];
}

export function isDataModelValue(value?: FormValue): boolean {
  return Array.isArray(value) && value?.length > 0 && !!(value[0] as DataModelValue)?.responseId;
}

/**
 * Deletes all keys in an object that start with a specific string.
 * * @param obj - The source object
 * @param prefix - The string to check at the start of each key
 * @returns A new object with matching keys removed
 */
export function deleteKeysByPrefix<T extends Record<string, unknown>>(
  obj: T,
  prefix: string,
): Partial<T> {
  return Object.entries(obj).reduce((acc, [key, value]) => {
    if (!key.startsWith(prefix)) {
      acc[key as keyof T] = value as T[keyof T];
    }
    return acc;
  }, {} as Partial<T>);
}
